/**
 * The shared plumbing behind every entity store (MAR-125).
 *
 * `documents`, `cases` and `clients` all need the same four mechanics: a cache keyed by id, a merge
 * on write, a removal that reaches every reference, and a way to turn an ordered id list back into
 * rows. Written out three times that is ~120 duplicated lines and three chances for the merge
 * semantics to drift apart — which is precisely how `useBrowser`, `useDocumentBrowser` and
 * `useCaseDocuments` ended up disagreeing about the same documents.
 *
 * **This shares plumbing, never policy.** No fetching, no endpoints, no view lists, no mutations
 * live here. Each store writes those out plainly, because that is the part where the stores actually
 * differ and where a shared abstraction would cost more than it saves.
 *
 * ## The cache/view split
 *
 * An entity store holds ONE copy of each row here, and expresses every surface as an ordered list of
 * ids resolved through `resolve()`. That is what makes a flat array insufficient: the same document
 * arrives from the folder listing, the grouped listing and search with different fields populated
 * and in different orders. Ids-as-views means a row can appear in three surfaces without existing
 * three times, and removing it once removes it everywhere.
 */

export interface EntityCache<T extends { id: string }> {
  /** The deduplicated truth, keyed by id. Read it directly for reactivity; write through the fns. */
  byId: Ref<Map<string, T>>
  /** One row, or null. */
  get: (id: string) => T | null
  has: (id: string) => boolean
  /** Merge rows in. See the note on merge semantics below. */
  upsert: (rows: T[]) => void
  /**
   * Merge fields into ONE row the cache already holds; a no-op for a row it does not. For a payload
   * that shares only some fields with the row (a detail, a bare record) — it must correct what is
   * cached without ever filing a partial row as a whole one.
   */
  patch: (id: string, fields: Partial<T>) => void
  /** Drop rows from the cache. Views must prune their own id lists — see `pruneIds`. */
  remove: (ids: string[]) => void
  /** Turn an ordered id list into rows, skipping ids the cache no longer holds. */
  resolve: (ids: string[]) => T[]
  /** Every cached row, unordered. For stores whose view IS "everything I know". */
  all: () => T[]
  clear: () => void
}

/**
 * Filter an id list down to ids still present in the cache.
 *
 * Exported because pruning is the view's job, not the cache's: only the store knows which lists it
 * keeps. Keeping this a plain function (rather than having `remove` reach into registered views)
 * keeps the data flow one-directional and greppable.
 */
export function pruneIds(ids: string[], removed: Set<string>): string[] {
  return ids.some(id => removed.has(id)) ? ids.filter(id => !removed.has(id)) : ids
}

/**
 * Copy-on-write writes for the `ref`-of-Map/Set views every entity store keeps (MAR-948).
 *
 * Each returns a NEW collection and leaves its input untouched, so a view is updated with one
 * assignment — one reactive tick — and a caller holding the old value never sees it change under it.
 * Written out by hand, this was fifteen `new Map(x.value); next.set(…); x.value = next` stanzas in
 * the documents store alone.
 */
export function mapWith<K, V>(map: Map<K, V>, key: K, value: V): Map<K, V> {
  return new Map(map).set(key, value)
}

export function mapWithout<K, V>(map: Map<K, V>, key: K): Map<K, V> {
  const next = new Map(map)
  next.delete(key)
  return next
}

export function setWith<T>(set: Set<T>, value: T): Set<T> {
  return new Set(set).add(value)
}

export function setWithout<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set)
  next.delete(value)
  return next
}

/** `pruneIds` over every list of a keyed map of id lists (a sheet's documents, a folder's children). */
export function pruneIdLists<K>(lists: Map<K, string[]>, removed: Set<string>): Map<K, string[]> {
  const next = new Map<K, string[]>()
  for (const [key, ids] of lists) next.set(key, pruneIds(ids, removed))
  return next
}

export function createEntityCache<T extends { id: string }>(): EntityCache<T> {
  const byId = ref<Map<string, T>>(new Map()) as Ref<Map<string, T>>

  function get(id: string): T | null {
    return byId.value.get(id) ?? null
  }

  function has(id: string): boolean {
    return byId.value.has(id)
  }

  /**
   * Shallow-merge, never replace.
   *
   * Two endpoints can describe the same entity with different fields populated — a grouped document
   * row carries the derived counts, a search hit may not. A blind overwrite would let whichever
   * response landed last blank fields the other had filled, which reads to the user as data
   * flickering away for no reason.
   *
   * A new Map is assigned rather than mutated in place: Vue tracks collection mutation, but
   * reassigning keeps this cheap to reason about alongside the `ref`-of-Map views elsewhere, and
   * makes every write a single reactive tick regardless of batch size.
   */
  function upsert(rows: T[]): void {
    if (rows.length === 0) return
    const next = new Map(byId.value)
    for (const row of rows) {
      const existing = next.get(row.id)
      next.set(row.id, existing ? { ...existing, ...row } : row)
    }
    byId.value = next
  }

  function patch(id: string, fields: Partial<T>): void {
    const existing = byId.value.get(id)
    if (existing) upsert([{ ...existing, ...fields }])
  }

  function remove(ids: string[]): void {
    if (ids.length === 0) return
    const next = new Map(byId.value)
    let changed = false
    for (const id of ids) {
      if (next.delete(id)) changed = true
    }
    if (changed) byId.value = next
  }

  function resolve(ids: string[]): T[] {
    const out: T[] = []
    for (const id of ids) {
      const row = byId.value.get(id)
      if (row) out.push(row)
    }
    return out
  }

  function all(): T[] {
    return [...byId.value.values()]
  }

  function clear(): void {
    byId.value = new Map()
  }

  return { byId, get, has, upsert, patch, remove, resolve, all, clear }
}
